import type { AssetReference } from '../systems/AssetLoader';
import backgroundsData from '../data/backgrounds.json';
import casesData from '../data/cases.json';
import charactersData from '../data/characters.json';
import evidencesData from '../data/evidences.json';
import galleryData from '../data/gallery.json';
import type {
  BackgroundDefinition,
  CaseDefinition,
  CharacterDefinition,
  CharacterExpressionDefinition,
  EvidenceDefinition
} from './types';

export type GalleryTabId = 'characters' | 'backgrounds' | 'evidences' | 'secrets';

export interface CharacterGalleryEntry {
  character: CharacterDefinition;
  caseIds: string[];
}

export interface BackgroundGalleryEntry {
  background: BackgroundDefinition;
  caseIds: string[];
}

export interface EvidenceGalleryEntry {
  evidence: EvidenceDefinition;
  caseIds: string[];
}

export interface SecretGalleryEntry {
  id: string;
  label: string;
  description?: string;
  assetPath?: string;
  caseIds: string[];
  unlock: 'finalCaseComplete';
}

interface GalleryData {
  characterCases?: Record<string, string[]>;
  backgroundCases?: Record<string, string[]>;
  secretImages?: Array<{
    id: string;
    label: string;
    description?: string;
    assetPath?: string;
    caseIds?: string[];
    unlock?: 'finalCaseComplete';
  }>;
}

const CASES = [...(casesData as { cases: CaseDefinition[] }).cases].sort(
  (left, right) => left.displayOrder - right.displayOrder
);
const CHARACTERS = (charactersData as { characters: CharacterDefinition[] }).characters;
const BACKGROUNDS = (backgroundsData as { backgrounds: BackgroundDefinition[] }).backgrounds;
const EVIDENCES = (evidencesData as { evidences: EvidenceDefinition[] }).evidences;
const GALLERY_DATA = galleryData as GalleryData;
const CHARACTER_ASSET_PREFIX = 'assets/images/characters/';
const GALLERY_ASSET_PREFIX = 'assets/images/gallery/';

export function listGalleryCases(): CaseDefinition[] {
  return CASES.map((caseDefinition) => ({ ...caseDefinition }));
}

export function listCharacterGalleryEntries(): CharacterGalleryEntry[] {
  return CHARACTERS.map((character) => ({
    character: cloneCharacter(character),
    caseIds: getMappedCaseIds(GALLERY_DATA.characterCases, character.id)
  })).filter((entry) => entry.caseIds.length > 0);
}

export function listBackgroundGalleryEntries(): BackgroundGalleryEntry[] {
  return BACKGROUNDS.map((background) => ({
    background: { ...background },
    caseIds: getMappedCaseIds(GALLERY_DATA.backgroundCases, background.id)
  })).filter((entry) => entry.caseIds.length > 0);
}

export function listEvidenceGalleryEntries(): EvidenceGalleryEntry[] {
  return EVIDENCES.map((evidence) => ({
    evidence: { ...evidence },
    caseIds: evidence.caseId ? [evidence.caseId] : []
  }));
}

export function listSecretGalleryEntries(): SecretGalleryEntry[] {
  return (GALLERY_DATA.secretImages ?? [])
    .filter((image) => image.id.length > 0 && image.label.length > 0)
    .map((image) => ({
      id: image.id,
      label: image.label,
      description: image.description,
      assetPath: image.assetPath,
      caseIds: normalizeCaseIds(image.caseIds),
      unlock: image.unlock ?? 'finalCaseComplete'
    }));
}

export function findGalleryCase(caseId: string): CaseDefinition | null {
  return CASES.find((caseDefinition) => caseDefinition.id === caseId) ?? null;
}

export function createCharacterExpressionImageReference(
  character: CharacterDefinition,
  expression: CharacterExpressionDefinition
): AssetReference {
  const fileName =
    expression.fileName ?? getFileNameFromPath(expression.assetPath) ?? `${character.id}.webp`;
  return {
    id: stripImageExtension(fileName),
    kind: 'character',
    path: expression.assetPath ?? `${CHARACTER_ASSET_PREFIX}${fileName}`
  };
}

export function createBackgroundGalleryImageReference(
  background: BackgroundDefinition
): AssetReference {
  return {
    id: background.id,
    kind: 'background',
    path: background.assetPath
  };
}

export function createEvidenceGalleryImageReference(evidence: EvidenceDefinition): AssetReference {
  return {
    id: evidence.imageId ?? evidence.id,
    kind: 'evidence',
    path: evidence.imageAssetPath
  };
}

export function createSecretGalleryImageReference(secret: SecretGalleryEntry): AssetReference {
  const kind = getAssetKindFromPath(secret.assetPath);
  return {
    id: secret.id,
    kind,
    path: secret.assetPath ?? `${GALLERY_ASSET_PREFIX}${secret.id}.webp`
  };
}

function getMappedCaseIds(mappings: Record<string, string[]> | undefined, id: string): string[] {
  return normalizeCaseIds(mappings?.[id]);
}

function normalizeCaseIds(caseIds: unknown): string[] {
  if (!Array.isArray(caseIds)) {
    return [];
  }

  const knownCaseIds = new Set(CASES.map((caseDefinition) => caseDefinition.id));
  return [...new Set(caseIds.filter((caseId): caseId is string => knownCaseIds.has(caseId)))];
}

function cloneCharacter(character: CharacterDefinition): CharacterDefinition {
  return {
    ...character,
    expressions: character.expressions.map((expression) => ({ ...expression }))
  };
}

function getFileNameFromPath(path: string | undefined): string | undefined {
  return path?.split('/').at(-1);
}

function stripImageExtension(fileName: string): string {
  return fileName.replace(/\.(png|webp|jpe?g)$/i, '');
}

function getAssetKindFromPath(path: string | undefined): AssetReference['kind'] {
  if (path?.startsWith('assets/images/backgrounds/')) {
    return 'background';
  }
  if (path?.startsWith('assets/images/characters/')) {
    return 'character';
  }
  if (path?.startsWith('assets/images/evidences/')) {
    return 'evidence';
  }
  if (path?.startsWith('assets/images/ui/')) {
    return 'ui';
  }
  return 'gallery';
}
